import type { StripeConnection } from '@/services/stripeIntegrationService';
import { StripeConnectionCard } from '@/components/automation/tools/StripeConnectionCard';
import React, { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Plus, Filter, LayoutList, Wrench } from 'lucide-react';
import { ToolCard } from '@/components/automation/tools/ToolCard';
import { CustomToolsTab } from '@/components/automation/tools/CustomToolsTab';
import { fetchPrebuildSelectedApps, selectPrebuiltActions } from '@/store/slices/prebuiltActionsSlice';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { PrebuiltAction } from '@/services/prebuiltActionsService';
import { usePageHeader } from '@/contexts/PageHeaderContext';

const ToolsPage = () => {
  const dispatch = useAppDispatch();
  const [stripe, setStripe] = useState<StripeConnection | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [integratedTools, setIntegratedTools] = useState<PrebuiltAction[]>([]);
  // const prebuiltActions = useSelector((state: RootState) => state.prebuiltActions.items);
  // console.log('prebuiltActions', prebuiltActions);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const {
    setHeaderData
  } = usePageHeader();
  const categories = ['all', 'Communication', 'Productivity', 'CRM', 'Project Management', 'E-commerce', 'Storage', 'Customer Support', 'Analytics', 'Finance'];
  const filteredTools = integratedTools?.filter(tool => {
    const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) || tool.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || tool.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Set header data for dynamic header
  useEffect(() => {
    const connectedCount = integratedTools.filter(tool => tool.connected).length + (stripe?.connected ? 1 : 0);
    const totalActions = integratedTools.reduce((acc, tool) => acc + (tool.totalActions || 0), 0);
    setHeaderData({
      title: 'Tools',
      description: 'Connect and manage external tools for your automations',
      icon: Wrench,
      stats: [{
        label: 'Available Tools',
        value: integratedTools.length + 1,
        color: 'text-gray-900',
        dotColor: 'bg-blue-500'
      }, {
        label: 'Connected',
        value: connectedCount,
        color: 'text-emerald-600',
        dotColor: 'bg-emerald-500'
      }, {
        label: 'Total Actions',
        value: totalActions + (stripe?.actions.length || 5),
        color: 'text-purple-600',
        dotColor: 'bg-purple-500'
      }]
    });
  }, [integratedTools, stripe, setHeaderData]);
  useEffect(() => {
    dispatch(fetchPrebuildSelectedApps()).unwrap().then(data => {
      setIntegratedTools(data);
    }).catch(() => setIntegratedTools([]));
  }, []);
  const handleConnect = (toolName: string) => {
    console.log('Connecting to:', toolName);
  };
  const handleDisconnect = (toolName: string) => {
    console.log('Disconnecting from:', toolName);
  };
  return <div className="min-h-screen bg-gray-50/30">
      <div className="max-w-7xl mx-auto p-6 space-y-8">
        {/* Header Section */}
        

        <Tabs defaultValue="integrated" className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-1">
            <TabsList className="grid w-full grid-cols-2 bg-gray-50">
              <TabsTrigger value="integrated" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Integrated Tools
              </TabsTrigger>
              <TabsTrigger value="custom" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Custom Tools
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="integrated" className="space-y-6">
            <StripeConnectionCard onChange={setStripe} />
            {integratedTools.length > 0 && <>
            {/* Search and Filter Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input placeholder="Search tools..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20" />
                </div>
                <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-[200px]">
                  {categories.map(category => <option key={category} value={category}>
                      {category === 'all' ? 'All Categories' : category}
                    </option>)}
                </select>
              </div>
            </div>

            {/* Tools List Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Available Tools ({filteredTools.length})
                  </h2>
                  <div className="text-sm text-gray-500">
                    {filteredTools.filter(tool => tool.connected).length} connected
                  </div>
                </div>
                
                <div className="space-y-1">
                  {filteredTools.map((tool, index) => <ToolCard key={index} tool={tool} onConnect={() => handleConnect(tool.name)} onDisconnect={() => handleDisconnect(tool.name)} />)}
                </div>
              </div>
            </div>

            {filteredTools.length === 0 && <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Filter className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No tools found</h3>
                  <p className="text-gray-600 max-w-sm mx-auto">
                    Try adjusting your search terms or filter criteria to find the tools you're looking for.
                  </p>
                </div>
              </div>}
            </>}
          </TabsContent>

          <TabsContent value="custom">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 min-h-[400px]">
              <CustomToolsTab />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>;
};
export default ToolsPage;